import { useEffect, useState } from 'react';

import { App, Button, Input, Modal, Typography } from 'antd';

import { useImpactCertificate } from 'v2/hooks/useImpactCertificate';
import type { ImpactReportView } from 'v2/interfaces/impactMeter';

import { IMPACT_CERTIFICATE_BACKGROUND_SRC, IMPACT_CERTIFICATE_NAME_MAX_LENGTH } from './constants';

type CertificateModalProps = {
  open: boolean;
  onClose: () => void;
  userId: number;
  defaultName: string;
  report: ImpactReportView;
};

function getSafeCertificateUrl(value?: string) {
  if (!value) {
    return null;
  }

  try {
    const url = new URL(value);
    const isHttps = url.protocol === 'https:';
    const isSameOrigin = url.origin === window.location.origin;

    return isHttps || isSameOrigin ? url.toString() : null;
  } catch {
    return null;
  }
}

const CERTIFICATE_TAB_PATH = '/impact-certificate-loading.html';
const CERTIFICATE_TAB_READY_TIMEOUT_MS = 3000;
const CERTIFICATE_TAB_POLL_MS = 50;

function openCertificateTab() {
  return window.open(CERTIFICATE_TAB_PATH, '_blank');
}

function waitForCertificateTab(popup: Window) {
  return new Promise<void>(resolve => {
    const startedAt = Date.now();

    const timer = window.setInterval(() => {
      const timedOut = Date.now() - startedAt >= CERTIFICATE_TAB_READY_TIMEOUT_MS;

      if (popup.closed || timedOut) {
        window.clearInterval(timer);
        resolve();
        return;
      }

      try {
        if (popup.document.body?.dataset.ready === 'true') {
          window.clearInterval(timer);
          resolve();
        }
      } catch {
        window.clearInterval(timer);
        resolve();
      }
    }, CERTIFICATE_TAB_POLL_MS);
  });
}

function CertificateModal({ open, onClose, userId, defaultName }: CertificateModalProps) {
  const { message } = App.useApp();
  const { mutateAsync, isPending } = useImpactCertificate();
  const [name, setName] = useState(defaultName.slice(0, IMPACT_CERTIFICATE_NAME_MAX_LENGTH));

  useEffect(() => {
    if (open) {
      setName(defaultName.slice(0, IMPACT_CERTIFICATE_NAME_MAX_LENGTH));
    }
  }, [defaultName, open]);

  const handleDownload = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      message.warning('Informe o nome que deve aparecer no certificado.');
      return;
    }

    const popup = openCertificateTab();

    if (!popup) {
      message.warning('Permita pop-ups para abrir o certificado em uma nova aba.');
      return;
    }

    try {
      const certificate = await mutateAsync({ userId, name: trimmedName });
      const certificateUrl = getSafeCertificateUrl(certificate?.url);

      if (!certificateUrl) {
        popup.close();
        message.error('O endereço do certificado retornado é inválido.');
        return;
      }

      await waitForCertificateTab(popup);

      if (popup.closed) {
        return;
      }

      popup.location.replace(certificateUrl);
      onClose();
    } catch {
      popup.close();
      message.error('Não foi possível gerar o certificado. Tente novamente.');
    }
  };

  return (
    <Modal
      wrapProps={{ id: 'impact-meter-certificate' }}
      open={open}
      onCancel={onClose}
      title='Seu certificado de Criador (a) de Impacto'
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type='primary' loading={isPending} disabled={isPending} onClick={handleDownload}>
            Baixar certificado
          </Button>
        </>
      }
    >
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-1'>
          <Typography.Text id='impact-meter-certificate-name-label'>
            Como você quer que seu nome apareça no certificado?
          </Typography.Text>
          <Input
            id='impact-meter-certificate-name'
            aria-labelledby='impact-meter-certificate-name-label'
            aria-describedby='impact-meter-certificate-hint'
            maxLength={IMPACT_CERTIFICATE_NAME_MAX_LENGTH}
            value={name}
            onChange={event => setName(event.target.value)}
          />
          <Typography.Text id='impact-meter-certificate-hint' type='secondary'>
            O nome é só para exibição. O número de impacto não pode ser editado.
          </Typography.Text>
        </div>

        <div className='@container relative overflow-hidden rounded-(--uizz-antd-border-radius-lg)'>
          <img alt='' aria-hidden='true' className='block w-full' src={IMPACT_CERTIFICATE_BACKGROUND_SRC} />
          <div className='absolute inset-x-[18%] top-[26%] flex h-[12%] items-center justify-center overflow-hidden text-center'>
            <Typography.Text className='line-clamp-2 w-full text-[3.4cqw] leading-tight font-semibold text-balance text-(--uizz-antd-color-white)'>
              {name.trim() || defaultName}
            </Typography.Text>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default CertificateModal;
